/**
 * Payment Service
 * Handles Razorpay checkout. Wallet top-up works on all platforms; membership
 * subscriptions on iOS use IAP (openSubscriptionCheckout remains blocked on iOS).
 *
 * @author Varun Pandey
 */

import { isNativeIOSApp } from '@/utils/platform';

declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface PaymentOptions {
  amount: number;
  orderId: string;
  name: string;
  description: string;
  email: string;
  phone: string;
  onSuccess: (response: any) => void;
  onFailure: (error: any) => void;
  /**
   * Optional extra Razorpay checkout options (merged into base options).
   * Use this to customize methods/blocks for specific flows (e.g. EMI-only).
   */
  razorpayOptions?: Record<string, any>;
}

export interface SubscriptionPaymentOptions {
  subscriptionId: string;
  name: string;
  description: string;
  email: string;
  phone: string;
  onSuccess: (response: any) => void;
  onFailure: (error: any) => void;
}

export class PaymentService {
  private static readonly SCRIPT_URL =
    "https://checkout.razorpay.com/v1/checkout.js";

  /**
   * Load Razorpay script dynamically
   */
  private static loadScript(): Promise<boolean> {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = this.SCRIPT_URL;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  /**
   * Open Razorpay one-time checkout (wallet top-up; membership EMI on non-iOS).
   */
  static async openCheckout(options: PaymentOptions): Promise<void> {
    const isScriptLoaded = await this.loadScript();
    if (!isScriptLoaded) {
      options.onFailure(
        new Error("Razorpay SDK failed to load. Are you online?"),
      );
      return;
    }

    const key =
      import.meta.env.VITE_RAZORPAY_API_KEY ||
      import.meta.env.VITE_TEST_RAZORPAY_API_KEY;

    const rzpOptions = {
      key,
      amount: options.amount, // Amount is already in paise from backend
      currency: "INR",
      name: "StarClinch",
      description: options.description,
      order_id: options.orderId,
      handler: (response: any) => {
        options.onSuccess(response);
      },
      prefill: {
        name: options.name,
        email: options.email,
        contact: options.phone,
      },
      theme: {
        color: "#F33363",
      },
      modal: {
        ondismiss: () => {
          options.onFailure({
            code: "PAYMENT_CANCELLED",
            description: "Payment cancelled by user",
          });
        },
      },
      ...(options.razorpayOptions || {}),
    };

    const rzp = new window.Razorpay(rzpOptions);
    rzp.open();
  }

  /**
   * Open Razorpay subscription checkout using subscription_id
   */
  static async openSubscriptionCheckout(
    options: SubscriptionPaymentOptions,
  ): Promise<void> {
    if (isNativeIOSApp()) {
      options.onFailure({
        code: 'RAZORPAY_NOT_AVAILABLE_ON_IOS',
        description: 'Use in-app purchase on iOS.',
      });
      return;
    }

    const isScriptLoaded = await this.loadScript();
    if (!isScriptLoaded) {
      options.onFailure(
        new Error("Razorpay SDK failed to load. Are you online?"),
      );
      return;
    }

    const key =
      import.meta.env.VITE_RAZORPAY_API_KEY ||
      import.meta.env.VITE_TEST_RAZORPAY_API_KEY;

    const rzpOptions = {
      key,
      subscription_id: options.subscriptionId,
      name: "StarClinch",
      description: options.description,
      handler: (response: any) => {
        options.onSuccess(response);
      },
      prefill: {
        name: options.name,
        email: options.email,
        contact: options.phone,
      },
      theme: {
        color: "#F33363",
      },
      modal: {
        ondismiss: () => {
          options.onFailure({
            code: "PAYMENT_CANCELLED",
            description: "Payment cancelled by user",
          });
        },
      },
    };

    const rzp = new window.Razorpay(rzpOptions);
    rzp.open();
  }
}
